<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue';
import { configuratorStateKey } from '../ProductConfiguratorKeys';
import ModalContainer from "@/components/ModalContainer.vue";
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import ErrorMessage from '../../forms/ErrorMessage.vue';
import ConfiguratorChevron from '../ConfiguratorChevron.vue';

const { configuratorState } = inject(configuratorStateKey);
const registerModal = inject('registerModal');
const unregisterModal = inject('unregisterModal');
const { websiteText } = useWebsiteTexts();
const emit = defineEmits(['goToStep', 'save', 'remove']);

const showWidthHeightError = ref(false);

const saveButtonText = computed(() => {
  return configuratorState.value.decorationSteps.confirmDecoration ? 'global__save' : 'global__next';
});

const placementAndTechniqueSelected = computed(() => {
  return !!(configuratorState.value.selections.selectedPlacement
    && configuratorState.value.selections.selectedTechnique);
});

const desiredWidth = computed(() => {
  return configuratorState.value.selections.desiredWidth || 0;
});

watch(desiredWidth, () => {
  showWidthHeightError.value = false;
});

const desiredHeight = computed(() => {
  return configuratorState.value.selections.desiredHeight || 0;
});

watch(desiredHeight, () => {
  showWidthHeightError.value = false;
});

const selectedTechnique = computed(() => {
  return configuratorState.value.selections.selectedTechnique;
});

watch(selectedTechnique, () => {
  showWidthHeightError.value = false;
});

const editUploadedFile = computed(() => (configuratorState.value.selections.fileUpload
  ? 'confirmDecoration'
  : 'uploadDecoration'));

const confirmationModalOpen = ref(false);

function openConfirmationModal() {
  registerModal('DecoratorRemove');
  confirmationModalOpen.value = true;
}

function closeConfirmationModal() {
  confirmationModalOpen.value = false;
  unregisterModal('DecoratorRemove');
}

function acceptConfirmation() {
  closeConfirmationModal();
  emit('remove');
}

function goToStepAndScrollToView() {
  if ((desiredWidth.value !== 0 && desiredHeight.value === 0) || (desiredWidth.value === 0 && desiredHeight.value !== 0)) {
    showWidthHeightError.value = true;
    return;
  }

  emit('goToStep', editUploadedFile.value);

  const configEl = document.getElementById('decoration-anchor');
  if (!configEl) return;

  const headerOffsetWithExtraPadding = 100;
  setTimeout(() => {
    window.scrollTo({
      top: configEl.offsetTop - headerOffsetWithExtraPadding,
      left: 0,
      behavior: 'smooth',
    });
  }, 300);
}
</script>
<template>
  <div class="decoration-bottom-menu">
    <div
      v-if="configuratorState.decorationSteps.placeDecoration
        || configuratorState.decorationSteps.pickTechnique"
      class="decoration-bottom-menu__decoration-info"
    >
      <template v-if="configuratorState.decorationSteps.placeDecoration">
        <p class="decoration-bottom-menu__question">
          {{ websiteText('decoration__decorate_placement_question').value }}
        </p>
        <p class="decoration-bottom-menu__tip">
          {{ websiteText('decoration__decorate_placement_tip').value }}
        </p>
      </template>
      <div class="decoration-bottom-menu__buttons">
        <button
          class="decoration-bottom-menu__back-button"
          @click="openConfirmationModal"
        >
          <img
            class="decoration-bottom-menu__back-button-arrow"
            src="@/static/icons/arrow_left_icon.svg"
          >
          {{ websiteText('global__back').value }}
        </button>
        <button
          class="decoration-bottom-menu__continue-button"
          @click="goToStepAndScrollToView()"
          :disabled="!placementAndTechniqueSelected"
        >
          {{ websiteText('global__next').value }}
          <ConfiguratorChevron
            :open="true"
            class="decoration-bottom-menu__continue-button-chevron"
          >
          </ConfiguratorChevron>
        </button>
      </div>
      <error-message
        v-if="showWidthHeightError"
        :message="websiteText('decoration__input_both_size').value"
      />
    </div>
    <div
      class="decoration-bottom-menu__file-info"
      v-if="configuratorState.decorationSteps.uploadDecoration
        || configuratorState.decorationSteps.confirmDecoration"
    >
      <button
        class="decoration-bottom-menu__back-button"
        @click="emit('goToStep', 'pickTechnique')"
      >
        <img
          class="decoration-bottom-menu__back-button-arrow"
          src="@/static/icons/arrow_left_icon.svg"
        >
        {{ websiteText('global__back').value }}
      </button>
      <button
        class="decoration-bottom-menu__save-button"
        @click="emit('save')"
        :disabled="!configuratorState.decorationSteps.confirmDecoration"
      >
        {{ websiteText(saveButtonText).value }}
        <ConfiguratorChevron
          v-if="configuratorState.decorationSteps.uploadDecoration"
          :open="true"
          class="decoration-bottom-menu__save-button-chevron"
        >
        </ConfiguratorChevron>
      </button>
    </div>
    <div
      class="decoration-bottom-menu__summary-remove"
      v-if="configuratorState.decorationSteps.decorationSummary"
    >
      <button
          class="decoration-bottom-menu__remove-button"
          @click="openConfirmationModal"
        >
          <img
            class="decoration-bottom-menu__remove-button-icon"
            src="@/static/icons/trash_icon.svg"
          >
          {{ websiteText('decoration__remove').value }}
        </button>
    </div>
    <ModalContainer
      v-if="confirmationModalOpen"
      :show-exit-button="true"
      position="center"
      @close-modal="closeConfirmationModal"
    >
    <div class="decoration-remove__modal">
      <p class="decoration-remove__modal-title">
        {{ websiteText('decoration__remove_title').value }}
      </p>
      <p class="decoration-remove__modal-subtitle">
        {{ websiteText('decoration__remove_subtitle').value }}
      </p>
      <div class="decoration-remove__modal-options">
        <button
          class="decoration-remove__modal-cancel secondary-button"
          @click="closeConfirmationModal"
        >
          {{ websiteText('decoration__remove_cancel').value }}
        </button>
        <button
          class="decoration-remove__modal-accept primary-button"
          @click="acceptConfirmation"
        >
          {{ websiteText('decoration__remove_accept').value }}
        </button>
      </div>
    </div>
    </ModalContainer>
  </div>
</template>

<style>
    .decoration-bottom-menu__question {
    margin: 0;
    font-size: 14px;
    line-height: 19px;
    font-weight: bold;
    color: var(--color-text-darker);

  }

  .decoration-bottom-menu__tip {
    margin: 0 0 15px;
    font-size: 14px;
    line-height: 24px;
    font-style: italic;
    color: var(--color-neutrals-80);
  }

  .decoration-bottom-menu__buttons {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .decoration-bottom-menu__continue-button,
  .decoration-bottom-menu__save-button {
    background: var(--color-brand-juniper-100);
    border-radius: 2px;
    color: var(--color-white);
    padding: 12px 15px;
    font-weight: bold;
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-width: 127px;
    white-space: nowrap;
    margin-left: auto;
    font-size: 15px;
    line-height: 16px;
  }

  .decoration-bottom-menu__save-button {
    justify-content: center;
  }

  .decoration-bottom-menu__continue-button:disabled,
  .decoration-bottom-menu__save-button:disabled {
    background: var(--color-neutrals-40);
    color: var(--color-neutrals-60);
    cursor: default;
  }

  .decoration-bottom-menu__continue-button-chevron,
  .decoration-bottom-menu__save-button-chevron {
    transform: rotate(-90deg);
    flex-shrink: 0;
    margin-left: 10px;
  }

  .decoration-bottom-menu__file-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .decoration-bottom-menu__back-button {
    border: none;
    color: var(--color-text-darker);
    background-color: var(--color-white);
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 16px;
    line-height: 24px;
    padding: 0;
  }

  .decoration-bottom-menu__back-button-arrow {
    margin-right: 10px;
  }

  .decoration-bottom-menu__summary-remove {
    border-top: 1px solid var(--color-neutrals-20);
  }

  .decoration-bottom-menu__remove-button {
    margin-top: 15px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: var(--color-neutrals-80);
    font-size: 14px;
    line-height: 19px;
  }

  .decoration-bottom-menu__remove-button-icon {
    margin-right: 5px;
  }

  /* Override the fixed 400px width on desktop */
  .decoration-bottom-menu .modal-container {
    width: auto;
    max-width: 462px;
  }

  .decoration-remove__modal {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 20px;
    position: relative;
  }

  .decoration-remove__modal-title {
    font-weight: 700;
    font-size: 24px;
    line-height: 32px;
    text-align: center;
    margin: 0 0 5px;
  }

  .decoration-remove__modal-subtitle {
    font-size: 14px;
    line-height: 24px;
    text-align: center;
    margin: 0 0 20px;
  }

  .decoration-remove__modal-options{
    display: grid;
    gap: 10px;
    width: 100%;
    padding: 0 20%;
  }

  button.decoration-remove__modal-cancel,
  button.decoration-remove__modal-accept {
    padding: 0;
    width: 100%;
  }

  @media (--desktop) {
    .decoration-remove__modal {
      justify-items: center;
      padding: 42px 40px 25px;
    }

    .decoration-remove__modal-title {
      font-size: 16px;
      line-height: 24px;
    }

    .decoration-remove__modal-options{
      grid-auto-flow: column;
      grid-template-columns: 1fr 1fr;
      padding: 0;
    }

    .decoration-remove__modal-cancel{
      margin-right: 10px;
    }
  }
</style>
